import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/layout/Logo";

export default function Signup() {
  const { fetchUser } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    businessName: "",
    ownerName: "",
    email: "",
    phone: "",
    username: "",
    password: "",
    category: "",
    city: "",
    agreeTerms: false
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreeTerms) {
      return toast.error("You must agree to the Terms and Privacy Policy.");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      // Backend automatically sets the cookie, just like login
      localStorage.setItem("token", data.token); // Optional if using pure cookies, but auth flow might expect it
      await fetchUser(); // Updates AuthContext
      toast.success("Account created successfully!");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  return (
    <div className="flex min-h-screen bg-appbg">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block fixed h-screen">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-700 to-blue-500" />
        <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-20 right-10 h-96 w-96 rounded-full bg-blue-300/20 blur-3xl" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Logo light />
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight">
              Start Automating your Local Growth.
            </h1>
            <p className="mt-4 max-w-md text-white/80">
              Create your account to unlock premium AI scheduling, review management, and comprehensive analytics.
            </p>
            <div className="mt-8 flex gap-6 text-sm">
              <div><p className="text-2xl font-bold">1 Min</p><p className="text-white/70">Setup</p></div>
              <div><p className="text-2xl font-bold">Free</p><p className="text-white/70">Trial</p></div>
              <div><p className="text-2xl font-bold">100%</p><p className="text-white/70">Automated</p></div>
            </div>
          </div>
          <p className="text-xs text-white/60">© {new Date().getFullYear()} AWebBee.com — GMB Automation Manager</p>
        </div>
      </div>

      {/* Right form - scrolling */}
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2 lg:ml-[50%]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 lg:hidden"><Logo /></div>
          <p className="text-overline">Get Started</p>
          <h2 className="mt-2 font-heading text-3xl font-bold text-slate-900">Create your account</h2>
          <p className="mt-2 text-sm text-slate-500">Enter your details to create your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Business Name</label>
                <input required value={form.businessName} onChange={e => update("businessName", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Owner Name</label>
                <input value={form.ownerName} onChange={e => update("ownerName", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Email Address</label>
                <input required type="email" value={form.email} onChange={e => update("email", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Phone</label>
                <input required type="tel" value={form.phone} onChange={e => update("phone", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Category</label>
                <input placeholder="e.g. Plumber" value={form.category} onChange={e => update("category", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">City</label>
                <input value={form.city} onChange={e => update("city", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Choose Username</label>
              <input required value={form.username} onChange={e => update("username", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Create Password</label>
              <div className="relative">
                <input
                  required
                  type={show ? "text" : "password"}
                  value={form.password}
                  onChange={e => update("password", e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 pr-11 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="py-2">
              <label className="flex items-start gap-2 text-sm text-slate-600">
                <Checkbox className="mt-1" checked={form.agreeTerms} onCheckedChange={(v) => update("agreeTerms", v)} /> 
                <span>I agree to the Terms of Service and Privacy Policy. I understand that I am creating a trial account.</span>
              </label>
            </div>

            <Button type="submit" disabled={loading} className="h-11 w-full rounded-xl bg-brand-700 text-white shadow-brand hover:bg-brand-800">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create Account <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
            
            <p className="mt-4 text-center text-sm text-slate-600">
              Already have an account? <Link to="/login" className="font-medium text-brand-600 hover:underline">Sign in</Link>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
